import React from 'react';
import cx from 'classnames';
import { RadioButtonsDecoration, CheckboxDecoration, DefaultDecoration } from './InputField.decorators';
import InputFieldProps from './InputField.types';
import Input from '../../atoms/Input';
import Select from '../../atoms/Select';
import Checkbox from '../../atoms/Checkbox';
import RadioButtons from '../../atoms/RadioButtons';
import PhoneInput from '../../atoms/PhoneInput';
import DateInput from '../DateInput';
import BusinessNameInput from '../../atoms/BusinessNameInput';
import IncrementalInput from '../IncrementalInput';
import ButtonGroup from '../../atoms/ButtonGroup';
import styles from './InputField.module.scss';
import BusinessDetails from '../../organisms/BusinessDetails';
import AddressLookup from '../../organisms/AddressLookup/AddressLookup';
import HiddenInput from '../../atoms/HiddenInput';
import OwnershipStructure from '../../organisms/OwnershipStructure';
import VerificationInput from '../VerificationInput';
import MaskedInput from '../../atoms/MaskedInput';
import Typeahead from '../Typeahead';
import LoqateAddressLookup from '../../organisms/LoqateAddressLookup';

/**
 * The InputField is a wrapper for an actual input field,
 * it ensure the accessibility rules of having an associated label
 */

const InputField = ({
  invalid,
  title,
  id,
  name,
  errorMessage,
  default: defaultValue,
  description,
  onChange,
  enum: options,
  oneOf,
  type,
  uiSchema,
  errors,
  clearError,
  value,
  pointer,
  className,
  ariaHidden = false,
  serviceCaller,
  dataCy,
  childDataSchema,
  setValue,
  toggleDisplay,
  hideAll,
}: InputFieldProps) => {
  // uiSchema is optional
  const uiSchemaComponentType = uiSchema?.component;

  // The component can be passed via the uiSchema
  const lookupComponentFromUiSchema = {
    select: Select,
    text: Input,
    checkbox: Checkbox,
    number: Input,
    radioButtons: RadioButtons,
    phoneInput: PhoneInput,
    dateInput: DateInput,
    verificationInput: VerificationInput,
    addressLookup: AddressLookup,
    businessNameInput: BusinessNameInput,
    incrementalInput: IncrementalInput,
    buttonGroup: ButtonGroup,
    businessDetails: BusinessDetails,
    hiddenInput: HiddenInput,
    ownershipStructure: OwnershipStructure,
    maskedInput: MaskedInput,
    typeahead: Typeahead,
    loqateAddressLookup: LoqateAddressLookup,
  };
  // The component can be inferred via the properties provided by the schema
  const lookupComponentFromDefaultType = {
    string: Input,
    array: Input,
    boolean: Checkbox,
    number: Input,
    password: Input,
  };

  // select the appropiate form element
  // @TODO - once we have graphical radio/select there maybe another 'else if'

  let ComponentType;
  if (uiSchemaComponentType) {
    // 1. use the component defined in the uiSchema
    ComponentType = lookupComponentFromUiSchema[uiSchema?.component];
  } else if (oneOf || options) {
    // 2. if the schema contains a oneOf or options then use a select
    ComponentType = lookupComponentFromUiSchema.select;
  } else {
    // 3. use the default component based on type
    ComponentType = lookupComponentFromDefaultType[type];
  }

  if (!ComponentType) return null; // unable to find component
  // enforce an id
  const inputId = id || `id_${Math.floor(Math.random() * 1000) + 1}`;
  const errorMsgHolderId = `${inputId}_errorMsg`;

  const getInputElement = () => {
    return (
      <ComponentType
        options={oneOf || options}
        name={name}
        id={inputId}
        defaultValue={defaultValue}
        onChange={onChange}
        label={title}
        value={value ?? ''}
        errors={errors}
        clearError={clearError}
        dataPointer={pointer}
        uiSchema={uiSchema}
        invalid={invalid}
        dataCy={dataCy}
        errorMsgHolderId={errorMsgHolderId}
        serviceCaller={serviceCaller}
        {...uiSchema?.properties}
        childDataSchema={childDataSchema}
        setValue={setValue}
        toggleDisplay={toggleDisplay}
        hideAll={hideAll}
        type={type}
      />
    );
  };

  const labelClasses: string = cx({
    [styles.label]: true,
    [styles.invalid]: invalid,
    [styles.checkboxLabel]: ComponentType === Checkbox,
    [styles.isVisuallyHidden]: uiSchema?.properties?.hiddenLabel,
  });

  const InputEl = getInputElement();

  if (ComponentType === HiddenInput) return InputEl;

  const isFullWidthComponent =
    uiSchema?.properties?.isFullWidth || uiSchema?.properties?.graphical || uiSchema?.properties?.icons !== undefined;

  if (ComponentType === RadioButtons) {
    return (
      <RadioButtonsDecoration
        className={className}
        labelClasses={labelClasses}
        ariaHidden={ariaHidden}
        title={title}
        description={description}
        errorMessage={errorMessage}
        isFullWidth={isFullWidthComponent}
        color={uiSchema?.properties?.color}
        hideError={uiSchema?.properties?.hideError}
        noMargin={uiSchema?.properties?.noMargin}
        errorMsgHolderId={errorMsgHolderId}
        removeDescriptionMargin={uiSchema?.properties?.removeDescriptionMargin}>
        {InputEl}
      </RadioButtonsDecoration>
    );
  }
  if (ComponentType === Checkbox) {
    return (
      <CheckboxDecoration
        labelClasses={labelClasses}
        className={className}
        ariaHidden={ariaHidden}
        title={title}
        description={description}
        errorMessage={errorMessage}
        inputId={inputId}
        isFullWidth={isFullWidthComponent}
        color={uiSchema?.properties?.color}
        hideError={uiSchema?.properties?.hideError}
        reducedMargin={uiSchema?.properties?.reducedMargin}
        noMargin={uiSchema?.properties?.noMargin}
        errorMsgHolderId={errorMsgHolderId}
        removeDescriptionMargin={uiSchema?.properties?.removeDescriptionMargin}
        hideEmptyError={uiSchema?.properties?.hideEmptyError}
        sizing={uiSchema?.properties?.sizing}>
        {InputEl}
      </CheckboxDecoration>
    );
  }

  return (
    <DefaultDecoration
      labelClasses={labelClasses}
      className={className}
      ariaHidden={ariaHidden}
      title={title}
      description={description}
      errorMessage={errorMessage}
      inputId={inputId}
      value={value}
      errorMsgHolderId={errorMsgHolderId}
      toolTip={uiSchema?.properties?.toolTip}
      isFullWidth={isFullWidthComponent}
      color={uiSchema?.properties?.color}
      hideError={uiSchema?.properties?.hideError}
      reducedMargin={uiSchema?.properties?.reducedMargin}
      noMargin={uiSchema?.properties?.noMargin}
      removeDescriptionMargin={uiSchema?.properties?.removeDescriptionMargin}
      componentType={uiSchemaComponentType}>
      {InputEl}
    </DefaultDecoration>
  );
};

export default InputField;
